import type { PropsWithChildren } from 'react';
import { createContext, useContext } from 'react';
import {
  CUSTOMERIO_CONFIG,
  CUSTOMERIO_SITE_ID,
  CUSTOMERIO_SITE_ID_VALID,
} from '../constants/customerio';

// const TRACKING_ACTIVE = process.env.NODE_ENV === 'production';
const TRACKING_ACTIVE = false;

export interface CustomerIOContextData {
  customerio?: CustomerIOService;
}

export interface CustomerIOContextProps extends PropsWithChildren {
  siteId?: string;
  config?: Partial<CustomerIOConfig>;
}

export interface CustomerIOConfig {
  debug?: boolean;
  autoTrackPages?: boolean;
  useInApp?: boolean;
}

declare global {
  interface Window {
    _cio?:
      | Array<any[]>
      | {
          identify: (data: {
            id: string;
            email?: string;
            [key: string]: any;
          }) => void;
          track: (eventName: string, data?: Record<string, any>) => void;
          page: (pageName?: string, data?: Record<string, any>) => void;
          load: () => void;
          on: (event: string, callback: () => void) => void;
          off: (event: string, callback: () => void) => void;
          push: (args: any[]) => void;
          [key: string]: any;
        };
  }
}

const context = createContext<CustomerIOContextData>(
  {} as CustomerIOContextData
);

const { Provider } = context;

export const useCustomerIOContext = () => useContext(context);

export const CustomerIOProvider = ({
  children,
  siteId,
  config,
}: CustomerIOContextProps) => {
  const instance = CustomerIOService.getInstance(siteId, config);

  return <Provider value={{ customerio: instance }}>{children}</Provider>;
};

export class CustomerIOService {
  private static instance: CustomerIOService;

  private siteId?: string;

  private config?: Partial<CustomerIOConfig>;

  private currentUserId?: string;

  private isInitialized = false;

  private initializationCheckInterval?: ReturnType<typeof setInterval>;

  private pendingCallbacks: Array<() => void> = [];

  private constructor(siteId?: string, config?: Partial<CustomerIOConfig>) {
    this.siteId = siteId || CUSTOMERIO_SITE_ID;
    this.config = { ...CUSTOMERIO_CONFIG, ...config };

    if (this.siteId && CUSTOMERIO_SITE_ID_VALID && TRACKING_ACTIVE) {
      this.loadScript();
    } else if (this.config?.debug && !TRACKING_ACTIVE) {
      console.warn(
        'Customer.io: Tracking is disabled in non-production environment.'
      );
    } else if (this.config?.debug) {
      console.warn(
        'Customer.io: Site ID is missing or invalid. Skipping initialization.'
      );
    }
  }

  static getInstance(
    siteId?: string,
    config?: Partial<CustomerIOConfig>
  ): CustomerIOService {
    if (!CustomerIOService.instance && TRACKING_ACTIVE) {
      CustomerIOService.instance = new CustomerIOService(siteId, config);
    }
    return CustomerIOService.instance;
  }

  private loadScript(): void {
    if (typeof window === 'undefined') {
      return;
    }

    if (document.getElementById('cio-tracker')) {
      this.isInitialized = true;
      return;
    }

    if (!window._cio || Array.isArray(window._cio)) {
      window._cio = [] as any;
    }

    const methods = [
      'load',
      'identify',
      'sidentify',
      'track',
      'page',
      'on',
      'off',
    ];
    methods.forEach((method) => {
      (window._cio as any)[method] = function (...args: any[]) {
        if (Array.isArray(window._cio)) {
          window._cio.push([method, ...args]);
        }
      };
    });

    const script = document.createElement('script');
    script.id = 'cio-tracker';
    script.async = true;
    script.setAttribute('data-site-id', this.siteId!);
    script.setAttribute(
      'data-auto-track-page',
      this.config?.autoTrackPages !== false ? 'true' : 'false'
    );
    script.setAttribute(
      'data-use-in-app',
      this.config?.useInApp !== false ? 'true' : 'false'
    );
    script.src = 'https://assets.customer.io/assets/track.js';

    script.onload = () => {
      this.isInitialized = true;
      if (this.config?.debug) {
        console.log('Customer.io: Script loaded successfully');
      }
      if (window._cio && !Array.isArray(window._cio) && window._cio.load) {
        window._cio.load();
      }
    };

    script.onerror = () => {
      if (this.config?.debug) {
        console.error('Customer.io: Failed to load script');
      }
    };

    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript?.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }

  private waitForInitialization(callback: () => void): void {
    if (this.isInitialized && window._cio && !Array.isArray(window._cio)) {
      callback();
      return;
    }

    this.pendingCallbacks.push(callback);

    if (!this.initializationCheckInterval) {
      let attempts = 0;
      const maxAttempts = 10;
      this.initializationCheckInterval = setInterval(() => {
        attempts++;
        const isReady =
          this.isInitialized && window._cio && !Array.isArray(window._cio);

        if (isReady) {
          this.clearInitializationCheck();
          this.pendingCallbacks.forEach((cb) => cb());
          this.pendingCallbacks = [];
        } else if (attempts >= maxAttempts) {
          this.clearInitializationCheck();
          if (this.config?.debug) {
            console.warn(
              'Customer.io: Initialization timeout. Executing callbacks anyway.'
            );
          }
          this.pendingCallbacks.forEach((cb) => cb());
          this.pendingCallbacks = [];
        }
      }, 1000);
    }
  }

  private clearInitializationCheck(): void {
    if (this.initializationCheckInterval) {
      clearInterval(this.initializationCheckInterval);
      this.initializationCheckInterval = undefined;
    }
  }

  identifyUser(userId?: string, userProperties?: Record<string, any>): void {
    if (!TRACKING_ACTIVE) {
      return;
    }

    if (!userId || userId.trim() === '') {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const cleanUserId = String(userId).trim();
    this.currentUserId = cleanUserId;

    if (this.config?.debug) {
      console.log('Customer.io: Identifying user:', cleanUserId);
    }

    this.waitForInitialization(() => {
      if (window._cio && !Array.isArray(window._cio) && window._cio.identify) {
        window._cio.identify({
          id: cleanUserId,
          ...userProperties,
        });
      }
    });
  }

  resetUser(): void {
    if (typeof window === 'undefined' || !window._cio) {
      return;
    }

    this.currentUserId = undefined;
  }

  getUserId() {
    return this.currentUserId || null;
  }

  trackEvent(eventName: string, eventData?: Record<string, any>): void {
    if (!TRACKING_ACTIVE) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    if (this.config?.debug) {
      console.log('Customer.io: Tracking event:', eventName);
    }

    this.waitForInitialization(() => {
      if (window._cio && !Array.isArray(window._cio) && window._cio.track) {
        window._cio.track(eventName, eventData || {});
      }
    });
  }

  trackPageView(pageName?: string, pageData?: Record<string, any>): void {
    if (!TRACKING_ACTIVE) {
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    this.waitForInitialization(() => {
      if (window._cio && !Array.isArray(window._cio) && window._cio.page) {
        window._cio.page(pageName, pageData);
      }
    });
  }

  updateUser(userId: string, userProperties: Record<string, any>): void {
    if (this.currentUserId !== userId) {
      this.currentUserId = userId;
    }
    this.identifyUser(userId, userProperties);
  }
}
